import React from 'react';
import { X, CheckCircle, Users, FileText, Trophy, CreditCard, ArrowRight } from 'lucide-react';

const ImportSuccessModal = ({ isOpen, onClose, syncResults }) => {
  if (!isOpen || !syncResults) return null;

  // Collect all unique family member names from all sync results
  const allFamilyMemberNames = new Set();
  Object.values(syncResults).forEach(result => {
    if (result.summary?.familyMemberNames && Array.isArray(result.summary.familyMemberNames)) {
      result.summary.familyMemberNames.forEach(name => allFamilyMemberNames.add(name));
    }
  });
  const uniqueFamilyMemberNames = Array.from(allFamilyMemberNames);

  // Calculate totals - include both added and updated
  const totalEntries = (syncResults.entries?.summary?.entriesAdded || 0) + (syncResults.entries?.summary?.entriesUpdated || 0);
  const totalResults = (syncResults.results?.summary?.showsAdded || 0) + (syncResults.results?.summary?.showsUpdated || 0);
  const totalMemberships = syncResults.memberships?.summary?.membershipsAdded || 0;
  const hasData = totalEntries > 0 || totalResults > 0 || totalMemberships > 0;
  
  // Get detailed counts for display
  const entriesAdded = syncResults.entries?.summary?.entriesAdded || 0;
  const entriesUpdated = syncResults.entries?.summary?.entriesUpdated || 0;
  const resultsAdded = syncResults.results?.summary?.showsAdded || 0;
  const resultsUpdated = syncResults.results?.summary?.showsUpdated || 0;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div className="flex items-center gap-3">
            <CheckCircle className="w-8 h-8 text-green-600" />
            <h2 className="text-2xl font-bold text-gray-900">Import Complete!</h2>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {hasData ? (
            <>
              {/* Import Summary */}
              <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                <h3 className="text-lg font-semibold text-gray-900 mb-3">Your Data Has Been Imported</h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {totalEntries > 0 && (
                    <div className="flex items-center gap-3">
                      <FileText className="w-6 h-6 text-blue-600" />
                      <div>
                        <p className="text-2xl font-bold text-gray-900">{totalEntries}</p>
                        <p className="text-sm text-gray-600">
                          {entriesAdded > 0 && entriesUpdated > 0 
                            ? `${entriesAdded} added, ${entriesUpdated} updated`
                            : entriesAdded > 0 
                            ? 'Entries added'
                            : entriesUpdated > 0
                            ? 'Entries updated'
                            : 'Entries'}
                        </p>
                      </div>
                    </div>
                  )}
                  {totalResults > 0 && (
                    <div className="flex items-center gap-3">
                      <Trophy className="w-6 h-6 text-yellow-600" />
                      <div>
                        <p className="text-2xl font-bold text-gray-900">{totalResults}</p>
                        <p className="text-sm text-gray-600">
                          {resultsAdded > 0 && resultsUpdated > 0 
                            ? `${resultsAdded} added, ${resultsUpdated} updated`
                            : resultsAdded > 0 
                            ? 'Results added'
                            : resultsUpdated > 0
                            ? 'Results updated'
                            : 'Results'}
                        </p>
                      </div>
                    </div>
                  )}
                  {totalMemberships > 0 && (
                    <div className="flex items-center gap-3">
                      <CreditCard className="w-6 h-6 text-purple-600" />
                      <div>
                        <p className="text-2xl font-bold text-gray-900">{totalMemberships}</p>
                        <p className="text-sm text-gray-600">Memberships</p>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Family Members Section */}
              {uniqueFamilyMemberNames.length > 0 && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <div className="flex items-start gap-3">
                    <Users className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
                    <div className="flex-1">
                      <h3 className="text-lg font-semibold text-gray-900 mb-2">
                        Family Members Added
                      </h3>
                      <p className="text-sm text-gray-700 mb-3">
                        We found data for the following family members and added them to your account:
                      </p>
                      <ul className="list-disc list-inside space-y-1 mb-3">
                        {uniqueFamilyMemberNames.map((name, index) => (
                          <li key={index} className="text-sm text-gray-800 font-medium">
                            {name}
                          </li>
                        ))}
                      </ul>
                      <div className="bg-white border border-blue-300 rounded p-3 mt-3">
                        <p className="text-sm text-gray-700">
                          <strong>Note:</strong> These family members have been set with the relationship "Other". 
                          You can update their relationships by going to{' '}
                          <span className="font-semibold text-blue-700">Family</span> in the menu.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Next Steps */}
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                <h3 className="text-lg font-semibold text-gray-900 mb-2">What's Next?</h3>
                <ul className="space-y-2 text-sm text-gray-700">
                  <li className="flex items-start gap-2">
                    <ArrowRight className="w-4 h-4 text-gray-500 mt-0.5 flex-shrink-0" />
                    <span>View your imported entries, results, and memberships on the home page</span>
                  </li>
                  {uniqueFamilyMemberNames.length > 0 && (
                    <li className="flex items-start gap-2">
                      <ArrowRight className="w-4 h-4 text-gray-500 mt-0.5 flex-shrink-0" />
                      <span>Update family member relationships in the Family menu</span>
                    </li>
                  )}
                  <li className="flex items-start gap-2">
                    <ArrowRight className="w-4 h-4 text-gray-500 mt-0.5 flex-shrink-0" />
                    <span>Use Settings &gt; KCI Rodeo Sync to keep your data up to date</span>
                  </li>
                </ul>
              </div>
            </>
          ) : (
            <div className="text-center py-8">
              <p className="text-gray-600 mb-4">
                Import completed, but no new data was found. Your data may already be up to date.
              </p>
              <p className="text-sm text-gray-500">
                You can sync manually anytime from Settings &gt; KCI Rodeo Sync.
              </p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex justify-end p-6 border-t border-gray-200">
          <button
            onClick={onClose}
            className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            Got It!
          </button>
        </div>
      </div>
    </div>
  );
};

export default ImportSuccessModal;

